<template>
  <div class="project-container">
    <header class="header">
      <div class="flex-container">
        <div class="image-container">
          <img :src="require('@/assets/image/ndi-global-page.png')" alt="Logo NDI 2025" class="header-image" loading="lazy"/>
        </div>
        <div class="text-container">
          <h1>{{ $t('ndi.title') }}</h1>
          <p class="subtitle">{{ $t('ndi.subtitle') }}</p>
        </div>
      </div>
    </header>

    <section class="about-project">
      <h2>{{ $t('ndi.aboutTitle') }}</h2>
      <div class="content-grid">
        <div class="text-block">
          <p v-html="$t('ndi.about1')"></p>
          <p v-html="$t('ndi.about2')"></p>
        </div>
        <div class="highlight-box">
           <div class="stat-item">
             <span class="mdi mdi-timer-outline stat-icon"></span>
             <span class="stat-text">24h Challenge</span>
           </div>
           <div class="stat-item">
             <span class="mdi mdi-api stat-icon"></span>
             <span class="stat-text">API-First Design</span>
           </div>
           <div class="stat-item">
             <span class="mdi mdi-infinity stat-icon"></span>
             <span class="stat-text">Full GitOps</span>
           </div>
        </div>
      </div>
    </section>

    <!-- Detailed DevOps Architecture Section -->
    <section class="devops-deep-dive">
      <h2>{{ $t('ndi.infraTitle') }}</h2>
      <div class="devops-grid">
        <div class="tech-card">
          <div class="tech-header">
            <span class="mdi mdi-rocket-launch-outline tech-icon"></span>
            <h4>{{ $t('ndi.gitopsTitle') }}</h4>
          </div>
          <p v-html="$t('ndi.gitopsDesc')"></p>
        </div>

        <div class="tech-card">
          <div class="tech-header">
            <span class="mdi mdi-layers-triple-outline tech-icon"></span>
            <h4>{{ $t('ndi.k8sTitle') }}</h4>
          </div>
          <p v-html="$t('ndi.k8sDesc')"></p>
        </div>

        <div class="tech-card full-width">
          <div class="tech-header">
            <span class="mdi mdi-sync tech-icon"></span>
            <h4>{{ $t('ndi.automationTitle') }}</h4>
          </div>
          <p v-html="$t('ndi.automationDesc')"></p>
        </div>
      </div>
    </section>

    <!-- Real-world Manifests (YAML blocks with Syntax Highlighting) -->
    <section class="concrete-manifests">
      <h2>{{ $t('ndi.concreteTitle') }}</h2>
      <div class="manifests-grid">
        <!-- 1. The Orchestrator (ApplicationSet) -->
        <div class="manifest-card">
          <div class="manifest-header">
            <span class="mdi mdi-auto-fix"></span>
            <h5>{{ $t('ndi.manifests.title1') }}</h5>
          </div>
          <div class="editor-window">
            <div class="editor-header">
              <div class="dots"><span class="r"></span><span class="y"></span><span class="g"></span></div>
              <span class="file-name">applicationset.yaml</span>
            </div>
            <div class="code-snippet-v3">
<pre><code><span class="y-comment"># Generator for GitLab MRs</span>
<span class="y-key">generators</span>:
  - <span class="y-key">pullRequest</span>:
      <span class="y-key">gitlab</span>:
        <span class="y-key">project</span>: <span class="y-str">ndi-2025/api</span>
        <span class="y-key">tokenRef</span>: {<span class="y-key">name</span>: <span class="y-str">token</span>, <span class="y-key">key</span>: <span class="y-str">token</span>}
<span class="y-key">template</span>:
  <span class="y-key">spec</span>:
    <span class="y-key">source</span>:
      <span class="y-key">targetRevision</span>: <span class="y-str">"{{branch}}"</span>
      <span class="y-key">path</span>: <span class="y-str">k8s/overlays/preview</span></code></pre>
            </div>
          </div>
          <p class="manifest-desc">{{ $t('ndi.manifests.desc1') }}</p>
        </div>

        <!-- 2. Dynamic Patching (Kustomize) -->
        <div class="manifest-card">
          <div class="manifest-header">
            <span class="mdi mdi-vector-point"></span>
            <h5>{{ $t('ndi.manifests.title2') }}</h5>
          </div>
          <div class="editor-window">
            <div class="editor-header">
              <div class="dots"><span class="r"></span><span class="y"></span><span class="g"></span></div>
              <span class="file-name">kustomization.yaml</span>
            </div>
            <div class="code-snippet-v3">
<pre><code><span class="y-comment"># Dynamic Hostname Patch</span>
- <span class="y-key">target</span>:
    <span class="y-key">kind</span>: <span class="y-str">IngressRoute</span>
  <span class="y-key">patch</span>: <span class="y-val">|-</span>
    <span class="y-str">- op: replace</span>
    <span class="y-str">  path: /spec/routes/0/match</span>
    <span class="y-str">  value: Host(`api-mr-{{number}}.ndi.com`)</span>

<span class="y-comment"># Dynamic DB Name</span>
- <span class="y-key">target</span>: {<span class="y-key">kind</span>: <span class="y-str">ConfigMap</span>}
  <span class="y-key">patch</span>: <span class="y-val">|-</span>
    <span class="y-str">- op: replace</span>
    <span class="y-str">  path: /data/DB_NAME</span>
    <span class="y-str">  value: ndi_mr{{number}}</span></code></pre>
            </div>
          </div>
          <p class="manifest-desc">{{ $t('ndi.manifests.desc2') }}</p>
        </div>

        <!-- 3. Resilience (Probes) -->
        <div class="manifest-card">
          <div class="manifest-header">
            <span class="mdi mdi-heart-pulse"></span>
            <h5>{{ $t('ndi.manifests.title3') }}</h5>
          </div>
          <div class="editor-window">
            <div class="editor-header">
              <div class="dots"><span class="r"></span><span class="y"></span><span class="g"></span></div>
              <span class="file-name">deployment.yaml</span>
            </div>
            <div class="code-snippet-v3">
<pre><code><span class="y-key">containers</span>:
  - <span class="y-key">name</span>: <span class="y-str">ndi-api</span>
    <span class="y-key">readinessProbe</span>:
      <span class="y-key">httpGet</span>: {<span class="y-key">path</span>: <span class="y-str">/system</span>, <span class="y-key">port</span>: <span class="y-val">6000</span>}
      <span class="y-key">initialDelaySeconds</span>: <span class="y-val">10</span>
    <span class="y-key">livenessProbe</span>:
      <span class="y-key">httpGet</span>: {<span class="y-key">path</span>: <span class="y-str">/system</span>, <span class="y-key">port</span>: <span class="y-val">6000</span>}
    <span class="y-key">resources</span>:
      <span class="y-key">requests</span>:
        <span class="y-key">cpu</span>: <span class="y-str">100m</span>
        <span class="y-key">memory</span>: <span class="y-str">256Mi</span></code></pre>
            </div>
          </div>
          <p class="manifest-desc">{{ $t('ndi.manifests.desc3') }}</p>
        </div>

        <!-- 4. Security (Sealed Secrets) -->
        <div class="manifest-card">
          <div class="manifest-header">
            <span class="mdi mdi-shield-key-outline"></span>
            <h5>{{ $t('ndi.manifests.title4') }}</h5>
          </div>
          <div class="editor-window">
            <div class="editor-header">
              <div class="dots"><span class="r"></span><span class="y"></span><span class="g"></span></div>
              <span class="file-name">sealed-secret.yaml</span>
            </div>
            <div class="code-snippet-v3">
<pre><code><span class="y-key">kind</span>: <span class="y-str">SealedSecret</span>
<span class="y-key">spec</span>:
  <span class="y-key">encryptedData</span>:
    <span class="y-key">token</span>: <span class="y-str">AgCDo1oTCVNud0KjUxtoy...</span>
<span class="y-key">template</span>:
  <span class="y-key">metadata</span>:
    <span class="y-key">name</span>: <span class="y-str">gitlab-token</span>
    <span class="y-key">namespace</span>: <span class="y-str">argocd</span></code></pre>
            </div>
          </div>
          <p class="manifest-desc">{{ $t('ndi.manifests.desc4') }}</p>
        </div>

        <!-- 5. Networking (Traefik Middlewares) -->
        <div class="manifest-card">
          <div class="manifest-header">
            <span class="mdi mdi-shield-outline"></span>
            <h5>{{ $t('ndi.manifests.title5') }}</h5>
          </div>
          <div class="editor-window">
            <div class="editor-header">
              <div class="dots"><span class="r"></span><span class="y"></span><span class="g"></span></div>
              <span class="file-name">middleware.yaml</span>
            </div>
            <div class="code-snippet-v3">
<pre><code><span class="y-key">kind</span>: <span class="y-str">Middleware</span>
<span class="y-key">spec</span>:
  <span class="y-key">headers</span>:
    <span class="y-key">accessControlAllowMethods</span>:
      - <span class="y-str">GET</span>, <span class="y-str">POST</span>, <span class="y-str">PUT</span>, <span class="y-str">DELETE</span>
    <span class="y-key">accessControlAllowOriginList</span>:
      - <span class="y-str">"*"</span>
    <span class="y-key">accessControlAllowCredentials</span>: <span class="y-val">true</span></code></pre>
            </div>
          </div>
          <p class="manifest-desc">{{ $t('ndi.manifests.desc5') }}</p>
        </div>

        <!-- 6. Continuous Delivery (Image Updater) -->
        <div class="manifest-card">
          <div class="manifest-header">
            <span class="mdi mdi-update"></span>
            <h5>{{ $t('ndi.manifests.title6') }}</h5>
          </div>
          <div class="editor-window">
            <div class="editor-header">
              <div class="dots"><span class="r"></span><span class="y"></span><span class="g"></span></div>
              <span class="file-name">annotations.yaml</span>
            </div>
            <div class="code-snippet-v3">
<pre><code><span class="y-key">annotations</span>:
  <span class="y-key">argocd-image-updater.../image-list</span>: <span class="y-str">ndi-api</span>
  <span class="y-key">argocd-image-updater.../strategy</span>: <span class="y-str">newest-build</span>
  <span class="y-key">argocd-image-updater.../allow-tags</span>: <span class="y-val">regexp:^preview-mr{{number}}-.*</span></code></pre>
            </div>
          </div>
          <p class="manifest-desc">{{ $t('ndi.manifests.desc6') }}</p>
        </div>
      </div>
    </section>

    <!-- Strategy Details (The Vertical Stack) -->
    <section class="strategy-details">
      <h2>{{ $t('ndi.techTitle') }}</h2>
      <div class="tech-grid">
        <div class="tech-card">
          <div class="tech-header">
            <span class="mdi mdi-file-code-outline tech-icon"></span>
            <h4>{{ $t('ndi.apiFirstTitle') }}</h4>
          </div>
          <p v-html="$t('ndi.apiFirstDesc')"></p>
        </div>
        <div class="tech-card">
          <div class="tech-header">
            <span class="mdi mdi-leaf tech-icon"></span>
            <h4>{{ $t('ndi.backendTitle') }}</h4>
          </div>
          <p v-html="$t('ndi.backendDesc')"></p>
        </div>
        <div class="tech-card">
          <div class="tech-header">
            <span class="mdi mdi-react tech-icon"></span>
            <h4>{{ $t('ndi.frontendTitle') }}</h4>
          </div>
          <p v-html="$t('ndi.frontendDesc')"></p>
        </div>
      </div>
    </section>

    <section class="visuals-section">
      <h2>Project Previews</h2>
      <div class="visuals-grid">
        <div class="visual-card">
          <h3>Global Interface</h3>
          <div class="image-wrapper">
            <img :src="require('@/assets/image/ndi-global-page.png')" alt="NDI Global Page" class="doc-image" loading="lazy"/>
          </div>
        </div>
        <div class="visual-card">
          <h3>Post Management</h3>
          <div class="image-wrapper">
            <img :src="require('@/assets/image/ndi-post-page.png')" alt="NDI Post Page" class="doc-image" loading="lazy"/>
          </div>
        </div>
        <div class="visual-card">
          <h3>Personal Profile</h3>
          <div class="image-wrapper">
            <img :src="require('@/assets/image/ndi-me-page.png')" alt="NDI Me Page" class="doc-image" loading="lazy"/>
          </div>
        </div>
      </div>
    </section>

    <section class="technical-details">
      <h2>{{ $t('ndi.learningsTitle') }}</h2>
      <div class="competencies-grid">
        <InfraCompetencyCard 
          icon="mdi-api"
          title="API-First"
          :description="$t('ndi.learning1')"
        />
        <InfraCompetencyCard 
          icon="mdi-sync"
          title="GitOps Preview"
          :description="$t('ndi.learning2')"
        />
        <InfraCompetencyCard 
          icon="mdi-docker"
          title="Multi-Arch CI"
          :description="$t('ndi.learning3')"
        />
        <InfraCompetencyCard 
          icon="mdi-timer-flash-outline"
          title="Rapid Delivery"
          :description="$t('ndi.learning4')"
        />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';
import InfraCompetencyCard from '@/component/project/infra/InfraCompetencyCard.vue';

@Component({
  components: {
    InfraCompetencyCard
  }
})
export default class NDIComponent extends Vue {
}
</script>

<style scoped>
.project-container {
  font-family: 'TTInterfaces', sans-serif;
  line-height: 1.7;
  color: var(--ln-text-primary);
  background-color: transparent;
  width: 100%;
  max-width: var(--ln-content-max-width);
  margin: 0 auto;
  padding: 2rem 1.5rem;
  box-sizing: border-box;
}

.header {
  text-align: center;
  margin-bottom: 2rem;
  padding: 2.5rem;
  background: var(--ln-glass-elevated);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-radius: var(--ln-radius-lg);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 12px 48px var(--ln-glass-shadow);
}

.flex-container {
  display: flex;
  align-items: center;
  gap: 3rem;
  flex-wrap: wrap;
  justify-content: center;
}

.image-container {
  width: 280px;
  flex-shrink: 0;
}

.text-container {
  flex: 1 1 400px;
  text-align: left;
}

.header h1 {
  font-size: clamp(2rem, 6vw, 3rem);
  font-weight: 700;
  color: var(--ln-text-primary);
  margin-bottom: 0.5rem;
  letter-spacing: -1px;
}

.subtitle {
  font-size: 1.25rem;
  color: var(--ln-text-secondary);
  font-weight: 500;
  opacity: 0.9;
}

.header-image {
  width: 100%;
  max-width: 280px;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  transition: transform 0.5s ease;
}

.header-image:hover {
  transform: scale(1.05) rotate(2deg);
}

section {
  margin-bottom: 2rem;
  padding: 2.5rem;
  background: var(--ln-glass-base);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 8px 32px var(--ln-glass-shadow);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
}

section:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 50px var(--ln-glass-shadow-elevated);
}

h2 {
  font-size: clamp(1.5rem, 5vw, 2.2rem);
  font-weight: 700;
  color: var(--ln-text-primary);
  margin-bottom: 2rem;
  position: relative;
  padding-bottom: 1rem;
}

h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 80px;
  height: 4px;
  background: linear-gradient(to right, var(--ln-teal-deep), var(--ln-terracotta-subtle));
  border-radius: 2px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 3rem;
  align-items: center;
}

.highlight-box {
  background: var(--ln-glass-surface);
  padding: 2rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border-strong);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-item:last-child { margin-bottom: 0; }
.stat-icon { font-size: 2.2rem; color: var(--ln-teal-deep); }
.stat-text { font-weight: 600; color: var(--ln-text-primary); }

/* DevOps Grid Layout */
.devops-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.tech-card.full-width {
  grid-column: 1 / -1;
}

/* Concrete Manifests Grid */
.manifests-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.manifest-card {
  background: var(--ln-glass-surface);
  padding: 1.2rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  display: flex;
  flex-direction: column;
}

.manifest-header {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 1rem;
  color: var(--ln-teal-deep);
}

.manifest-header h5 {
  margin: 0;
  font-weight: 700;
  font-size: 1rem;
  color: var(--ln-text-primary);
}

/* Editor Window Look */
.editor-window {
  background: #0d1117;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  margin-bottom: 1rem;
  border: 1px solid rgba(255,255,255,0.1);
  flex-grow: 1;
}

.editor-header {
  background: #161b22;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}

.dots {
  display: flex;
  gap: 0.4rem;
}

.dots span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dots .r { background: #ff5f56; }
.dots .y { background: #ffbd2e; }
.dots .g { background: #27c93f; }

.file-name {
  color: #8b949e;
  font-size: 0.75rem;
  font-family: monospace;
}

.code-snippet-v3 {
  padding: 1rem;
  font-family: 'Fira Code', 'Courier New', monospace;
  font-size: 0.8rem;
  overflow-x: auto;
  text-align: left;
}

.code-snippet-v3 pre {
  margin: 0;
}

/* YAML Syntax Highlighting */
.y-key { color: #79c0ff; } /* Blue for keys */
.y-str { color: #a5d6ff; } /* Light blue for strings */
.y-val { color: #ffa657; } /* Orange for values/literals */
.y-comment { color: #8b949e; font-style: italic; } /* Gray for comments */

.manifest-desc {
  font-size: 0.85rem;
  color: var(--ln-text-secondary);
  margin: 0;
  text-align: left;
}

.tech-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
}

.tech-card {
  display: flex;
  flex-direction: column;
  background: var(--ln-glass-surface);
  padding: 2rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  text-align: left;
}

.tech-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--ln-glass-border);
}

.tech-icon { font-size: 2rem; color: var(--ln-teal-deep); }
.tech-header h4 { margin: 0; color: var(--ln-text-primary); font-size: 1.2rem; }

.tech-card p {
  font-size: 0.95rem;
  color: var(--ln-text-secondary);
  line-height: 1.6;
  margin: 0;
}

/* Visuals grid */
.visuals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.visual-card {
  display: flex;
  flex-direction: column;
}

.visual-card h3 {
  margin-bottom: 1.5rem;
  color: var(--ln-text-primary);
  font-size: 1.2rem;
  text-align: center;
}

.image-wrapper {
  background: var(--ln-glass-surface);
  padding: 1rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.doc-image {
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 8px 24px var(--ln-glass-shadow);
  transition: transform 0.4s var(--ln-ease-smooth);
}

.doc-image:hover {
  transform: scale(1.02);
}

.competencies-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

@media (max-width: 1200px) {
  .competencies-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 992px) {
  .content-grid { grid-template-columns: 1fr; }
  .devops-grid { grid-template-columns: 1fr; }
}

@media (max-width: 768px) {
  .header { padding: 1.5rem; }
  .flex-container { gap: 1rem; flex-direction: column; }
  .text-container { text-align: center; flex: 1 1 auto; }
  section { padding: 1.5rem; }
  .competencies-grid { grid-template-columns: 1fr; }
}
</style>
